import { EDecimalToHex } from "@styles/common-colors";
import { CSSProperties } from "@mui/material/styles/createMixins";
import { Breakpoint, SxProps, Theme } from "@mui/material";
import {
  BreakpointExtended,
  DEFAULT_TEXT_FONT_SIZE,
  PAGE_BREAKPOINTS_EXTENDED,
} from "@styles/common-styles";
import { ConvertUtils } from "@stellar/utils";
import { blue, neutral } from "@flat-ui";

/** Defines the size in pixels for the header bar */
export const HEADER_HEIGHT = "3.75rem";

/** This constant is used to show a dash when no data is available */
export const DASH = "-";

/** The width of the sidebar when in minified condition */
export const SIDEBAR_WIDTH_MINIFIED = 65;

/** The width of the sidebar when in open condition */
export const SIDEBAR_WIDTH_EXPANDED = 210;

/**
 * Defines the padding between the tab selector and the content.
 */
export const TABS_CONTENT_PADDING = "12px";

/**
 * CSS class to only show an element when the mouse is hovering over it.
 */
export const SHOW_ON_HOVER_CLASS = "show-on-hover";

/**
 * CSS class used to hide an element by default and show it only when its parent
 * (or a specific ancestor) is being hovered.
 *
 * This class should be used in combination with CSS that targets the hover state
 * of a container to conditionally reveal the element.
 */
export const HIDE_ON_HOVER_CLASS = "hide-on-hover";

const THOUSAND = 1000;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const GIGA_BYTE_SIZE = 1024 ** 3;

/** Default CSS style to apply when the button is hovered. */
export const hoverStyle: CSSProperties = {
  backgroundColor: addTransparency({
    color: neutral[500],
    alpha: EDecimalToHex.forty,
  }),
  ...getSvgColoredIconCss(blue[500]),
};

/** Default CSS style to apply when the button is clicked. */
export const clickStyle: CSSProperties = {
  backgroundColor: addTransparency({
    color: neutral[500],
    alpha: EDecimalToHex.seventy,
  }),
  ...getSvgColoredIconCss(blue[500]),
};

/**
 * Adds a prefix hash "#" to a string if there's no one.
 * This is particularly useful for HEX colors
 *
 * @param str "The value to add the hash. E.g. FFFFFF"
 * @returns The value including the prefixed hash, e.g. "#FFFFFF".
 */
export function addPrefixHashIfMissing(str: string): string {
  const hex = str ?? "";
  return hex.startsWith("#") ? hex : `#${hex}`;
}

/**
 * Exports the CSS properties to color an SVG icon, It colors all possible svg tags,
 * except the ones that have fill="none" because those ones are not colored and
 * instead are used as content wrappers.
 *
 * @param color CSS color to be used.
 */
export function getSvgColoredIconCss(color: string): CSSProperties {
  const hexColor = addPrefixHashIfMissing(color ?? "");

  /* eslint-disable @typescript-eslint/naming-convention -- The namings are CSS selectors */
  return {
    "path:not([fill='none'])": { fill: hexColor },
    "rect:not([fill='none'])": { fill: hexColor },
    "circle:not([fill='none'])": { fill: hexColor },
    "ellipse:not([fill='none'])": { fill: hexColor },
    "line:not([fill='none'])": { fill: hexColor },
    "polyline:not([fill='none'])": { fill: hexColor },
    "polygon:not([fill='none'])": { fill: hexColor },
  };
  /* eslint-enable */
}

interface AddTransparencyProps {
  /**
   * Color to add transparency. Should be in HEX format
   * including the prefix "#". E.g. "#FFFFFF"
   */
  color: string;

  /** Value for transparency in HEX format, ranging from "00" to "FF" */
  alpha: EDecimalToHex;
}

/**
 * Adds the alpha value for transparency to an HEX color.
 *
 * @returns The final HEXA color.
 */
export function addTransparency({
  color,
  alpha,
}: AddTransparencyProps): string {
  return `${addPrefixHashIfMissing(color ?? "")}${alpha ?? ""}`;
}

/** Copies some text into to the clipboard */
export function copyToClipboard(text: string): void {
  if (navigator && navigator.clipboard) {
    // If the native function is available, use it.
    void navigator.clipboard.writeText(text);
    return;
  }

  // Otherwise this is a workaround for browsers that don't support the native function.
  const copyArea = document.createElement("textarea");
  copyArea.value = text;
  // Needed so that it works in all browsers that generally support document.execCommand('copy').
  document.body.appendChild(copyArea);
  copyArea.select();

  // Should never throw an exception according to https://developer.mozilla.org/de/docs/Web/API/Document/execCommand
  document.execCommand("copy");
  document.body.removeChild(copyArea);
}

/**
 * Returns a CSS box-shadow for a bottom border.
 *
 * @param thickness The thickness of the border.
 * @param color The color of the border.
 * @returns The CSS box-shadow.
 */
export function getBottomBorderWithBoxShadow({
  thickness = "1px",
  color = blue[500],
}: {
  thickness?: string | number;
  color?: string | number;
}): CSSProperties["boxShadow"] {
  return `0 ${thickness} 0 0 ${color}`;
}

/**
 * Exports the border CSS property.
 *
 * @param color Color of the border.
 * @param width Optional width of the border. Defaults to "1px".
 * @returns CSS property to be used in border.
 */
export function border(color: string, width = "1px"): CSSProperties["border"] {
  return `${width} solid ${color}`;
}

/** Width of the cards depending on the breakpoint */
export const cardWidth: {
  [key in Extract<Breakpoint, "xs" | "sm">]: CSSProperties["width"];
} = {
  xs: "100%",
  sm: "250px",
};

/** Exports properties to be used in cards to show projects, groups, etc. */
export const cardProperties: {
  height: CSSProperties["height"];
  marginTop: CSSProperties["marginTop"];
  boxShadow: CSSProperties["boxShadow"];
  borderRadius: CSSProperties["borderRadius"];
  creationDatePaddingTop: CSSProperties["paddingTop"];
} = {
  height: "311px",
  marginTop: "20px",
  boxShadow: `0 0 0 1px ${neutral[200]}`,
  borderRadius: "4px",
  creationDatePaddingTop: "14px",
};

/**
 * Returns the breakpoint name based on the width of the screen.
 *
 * @param width The width of the screen.
 * @returns The name of the breakpoint.
 */
export function getBreakpointName(width: number): BreakpointExtended {
  for (let i = 0; i < Object.values(PAGE_BREAKPOINTS_EXTENDED).length; i++) {
    if (width < Object.values(PAGE_BREAKPOINTS_EXTENDED)[i]) {
      return Object.keys(PAGE_BREAKPOINTS_EXTENDED)[
        i - 1
      ] as BreakpointExtended;
    }
  }
  return "xxxl";
}

/**
 * Defines the style properties for different parts of an Autocomplete component
 */
interface AutocompleteStylesProps {
  /** Styles for the dropdown option items */
  option: {
    sx: SxProps<Theme>;
  };

  /** Styles for the dropdown no options found message */
  noOption: {
    sx: SxProps<Theme>;
  };

  /** Styles for the text content within list items */
  listItemText: {
    sx: SxProps<Theme>;
  };

  /** Styles for the dropdown paper container */
  paper: {
    sx: SxProps<Theme>;
  };

  /** Styles for the input field */
  input: {
    sx: SxProps<Theme>;
  };

  /** Styles for icons container */
  iconWrapper: {
    sx: SxProps<Theme>;
  };
}

/**
 * Returns the styles for the Autocomplete component.
 */
export function getAutocompleteStyles(): AutocompleteStylesProps {
  const selectedOptionStyles = {
    backgroundColor: addTransparency({
      color: neutral[500],
      alpha: EDecimalToHex.forty,
    }),
    "& .MuiListItemText-primary": {
      color: blue[500],
    },
  };
  const styles = {
    option: {
      sx: {
        my: 0,
        textWrap: "wrap",
        "&:not(:first-of-type)": {
          mt: "4px",
        },
        "&.MuiAutocomplete-option": {
          padding: "8px",
        },
        "& .MuiAutocomplete-listbox": {
          padding: 0,
        },
        "&.MuiAutocomplete-option[aria-selected='true']": {
          ...selectedOptionStyles,
        },
        "&.MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
          ...selectedOptionStyles,
        },
        "&:hover": {
          backgroundColor: addTransparency({
            color: neutral[500],
            alpha: EDecimalToHex.twentySix,
          }),
        },
        "&:hover .MuiListItemText-primary": {
          color: blue[500],
        },
      },
    },
    noOption: {
      sx: {
        fontSize: DEFAULT_TEXT_FONT_SIZE,
        color: neutral[800],
        opacity: 1,
        textDecoration: "none",
      },
    },
    listItemText: {
      sx: {
        height: "38px",
        "& .MuiListItemText-primary": {
          fontSize: DEFAULT_TEXT_FONT_SIZE,
          fontWeight: 600,
          color: neutral[800],
        },
        "& .MuiListItemText-secondary": {
          fontSize: DEFAULT_TEXT_FONT_SIZE,
          fontWeight: 400,
          color: neutral[600],
        },
      },
    },
    paper: {
      sx: {
        paddingX: "8px",
        gap: "10px",
        border: `1px solid ${neutral[200]}`,
        boxShadow: `0px 3px 3px 0px ${addTransparency({
          color: neutral[600],
          alpha: EDecimalToHex.twenty,
        })}`,
        borderRadius: "4px",
      },
    },
    input: {
      sx: {
        "& .MuiOutlinedInput-root": { paddingY: "2px" },
      },
    },
    iconWrapper: {
      sx: {
        height: "22px",
        width: "22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": {
          height: "16px",
          width: "16px",
        },
      },
    },
  };

  return styles;
}

/**
 * Checks if the storage size is less than 1 TB, which means it should be displayed in GB
 * If max value is smaller than 1 TB, then it return true, otherwise false
 */
function shouldShowGBStorage(byteSize: number): boolean {
  return byteSize < THOUSAND * GIGA_BYTE_SIZE;
}

/** Formats the storage size in a human-readable format, converting bytes to GB or TB as appropriate */
export function formatStorageSize(byteSize: number): number {
  if (byteSize < 0) {
    return 0;
  }

  if (shouldShowGBStorage(byteSize)) {
    return ConvertUtils.bytesToGb(byteSize);
  }

  return ConvertUtils.bytesToTb(byteSize);
}

/** Returns the unit based on the maximum value. If max value is undefined or small than 1 TB, then it return GB, otherwise TB */
export function getStorageUnit(maxBytes: number | undefined): "GB" | "TB" {
  if (maxBytes === undefined || maxBytes < 0) {
    return "GB";
  }

  if (shouldShowGBStorage(maxBytes)) {
    return "GB";
  }

  return "TB";
}

/**
 * Gets whether the browser supports hover interactions.
 * @returns True if the browser supports hover, false otherwise or the support cannot be determined.
 */
export function getIsHoverSupported(): boolean {
  try {
    return window.matchMedia("(hover: hover)").matches;
  } catch {
    // If the browser or test suit does not support `matchMedia` method then set as if hover is not supported
    return false;
  }
}
